import React, { useState, useEffect } from 'react';
import { Avatar, Dropdown, Typography, Space, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  ProfileOutlined,
  CreditCardOutlined,
  LoginOutlined,
  UserAddOutlined,
  CrownOutlined
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const { Text } = Typography;

// 现代化样式组件
const AuthButtonsContainer = styled(Space)`
  @media (max-width: 768px) {
    .auth-button {
      padding: 8px 16px !important;
      font-size: 0.9rem !important;
      height: 36px !important;
    }
  }
`;

// 移动端圆形图标按钮容器
const MobileAuthContainer = styled(Space)`
  gap: 8px;
`;

// 移动端圆形图标按钮
const MobileIconButton = styled(Button)`
  &.ant-btn {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &.login-button {
      border: 1px solid rgba(102, 126, 234, 0.3);
      background: rgba(255, 255, 255, 0.8);
      backdrop-filter: blur(10px);
      color: #667eea;
      
      &:hover {
        border-color: #667eea;
        background: rgba(102, 126, 234, 0.1);
        color: #667eea;
        transform: translateY(-2px);
        box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
      }
    }
    
    &.register-button {
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      border: none;
      color: white;
      
      &:hover {
        background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
        transform: translateY(-2px);
        box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
      }
    }
    
    .anticon {
      font-size: 18px;
    }
  }
`;

const LoginButton = styled(Button)`
  &.ant-btn {
    border: 1px solid rgba(102, 126, 234, 0.3);
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.8);
    backdrop-filter: blur(10px);
    color: #667eea;
    font-weight: 500;
    height: 40px;
    padding: 0 20px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      border-color: #667eea;
      background: rgba(102, 126, 234, 0.1);
      color: #667eea;
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(102, 126, 234, 0.15);
    }
    
    .anticon {
      font-size: 1rem;
    }
  }
`;

const RegisterButton = styled(Button)`
  &.ant-btn {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border: none;
    border-radius: 12px;
    color: white;
    font-weight: 600;
    height: 40px;
    padding: 0 20px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    
    &:hover {
      background: linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%);
      transform: translateY(-2px);
      box-shadow: 0 8px 32px rgba(102, 126, 234, 0.3);
    }
    
    .anticon {
      font-size: 1rem;
    }
  }
`;

const UserContainer = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: rgba(255, 255, 255, 0.95);
    border-color: rgba(102, 126, 234, 0.3);
    transform: translateY(-2px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 6px 8px;
    gap: 8px;
  }
`;

const StyledAvatar = styled(Avatar)`
  border: 2px solid rgba(102, 126, 234, 0.2);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  
  .anticon {
    color: white;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const UserName = styled(Text)`
  &.ant-typography {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    line-height: 1.2;
    margin: 0;
  }
`;

const UserStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StatusText = styled(Text)`
  &.ant-typography {
    font-size: 12px;
    color: #64748b;
    line-height: 1;
    margin: 0;
  }
`;

const PremiumIcon = styled(CrownOutlined)`
  font-size: 12px;
  color: #f59e0b;
`;

const DropdownMenu = styled.div`
  .ant-dropdown-menu {
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    padding: 8px;
    
    .ant-dropdown-menu-item {
      border-radius: 8px;
      padding: 12px 16px;
      margin: 2px 0;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(102, 126, 234, 0.1);
        color: #667eea;
      }
      
      .anticon {
        font-size: 1.1rem;
        margin-right: 12px;
      }
    }
    
    .ant-dropdown-menu-item-divider {
      margin: 8px 0;
      background-color: rgba(0, 0, 0, 0.06);
    }
  }
`;

interface UserAvatarProps {
  onNavigate?: () => void; // 导航后的回调函数，用于关闭移动端侧边栏
}

const UserAvatar: React.FC<UserAvatarProps> = ({ onNavigate }) => {
  const navigate = useNavigate();
  const { state, logout, loginWithUser } = useAuth();
  const { t } = useTranslation();
  const [isMobile, setIsMobile] = useState(false);

  // 检测移动端
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // 如果用户未登录，显示登录按钮
  if (!state.isAuthenticated) {
    const isLoginPage = window.location.pathname.startsWith('/login');
    
    // 移动端显示圆形图标按钮
    if (isMobile) {
      return (
        <MobileAuthContainer>
          <MobileIconButton 
            className="login-button"
            icon={<LoginOutlined />}
            onClick={() => {
              if (isLoginPage) {
                navigate('/login');
              } else {
                navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
              }
              onNavigate?.(); // 导航后关闭侧边栏
            }}
            title={t('auth.login')}
          />
          <MobileIconButton 
            className="register-button"
            icon={<UserAddOutlined />}
            onClick={() => {
              navigate('/register');
              onNavigate?.(); // 导航后关闭侧边栏
            }}
            title={t('auth.register')}
          />
        </MobileAuthContainer>
      );
    }
    
    // PC端显示原有的带文字按钮
    return (
      <AuthButtonsContainer>
        <LoginButton 
          icon={<LoginOutlined />}
          onClick={() => {
            if (isLoginPage) {
              navigate('/login');
            } else {
              navigate(`/login?returnUrl=${encodeURIComponent(window.location.pathname + window.location.search)}`);
            }
            onNavigate?.(); // 导航后关闭侧边栏
          }}
          className="auth-button"
        >
          {t('auth.login')}
        </LoginButton>
        <RegisterButton 
          icon={<UserAddOutlined />}
          onClick={() => {
            navigate('/register');
            onNavigate?.(); // 导航后关闭侧边栏
          }}
          className="auth-button"
        >
          {t('auth.register')}
        </RegisterButton>
      </AuthButtonsContainer>
    );
  }

  const { user } = state;

  // 用户菜单项
  const menuItems: MenuProps['items'] = [
    {
      key: 'profile',
      icon: <ProfileOutlined />,
      label: t('navigation.profile'),
      onClick: () => {
        navigate('/profile');
        onNavigate?.(); // 导航后调用回调函数（用于关闭移动端侧边栏）
      },
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: t('navigation.settings'),
      onClick: () => {
        navigate('/settings');
        onNavigate?.(); // 导航后调用回调函数（用于关闭移动端侧边栏）
      },
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('navigation.logout'),
      onClick: () => {
        logout();
        navigate('/');
        onNavigate?.(); // 注销后也关闭侧边栏
      },
    },
  ];

  // 获取用户显示名称
  const getUserDisplayName = () => {
    if (user?.display_name) {
      return user.display_name;
    }
    if (user?.email) {
      // 如果没有显示名，使用邮箱的用户名部分
      return user.email.split('@')[0];
    }
    return '用户';
  };

  // 获取用户状态文本
  const getUserStatusText = () => {
    if (!user) return 'Free';
    return user.subscription_status === 'premium' ? 'Premium' : 'Free';
  };

  return (
    <DropdownMenu>
      <Dropdown
        menu={{ items: menuItems }}
        placement="bottomRight"
        trigger={['click']}
        overlayClassName="modern-dropdown"
      >
        <UserContainer>
          <StyledAvatar
            size={32}
            src={user?.avatar_url}
            icon={<UserOutlined />}
          />
          <UserInfo>
            <UserName>
              {getUserDisplayName()}
            </UserName>
            <UserStatus>
              {user?.subscription_status === 'premium' && <PremiumIcon />}
              <StatusText>
                {getUserStatusText()}
              </StatusText>
            </UserStatus>
          </UserInfo>
        </UserContainer>
      </Dropdown>
    </DropdownMenu>
  );
};

export default UserAvatar; 